import React from 'react';
import { ShieldCheck } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { StatutBadge } from './ui/StatutBadge';
import { libelle } from '../lib/libelles';
import { resumeSurveillance } from '../lib/surveillanceWhatsapp';
import { cn } from '../lib/utils';

/**
 * « Surveillance automatique » de l'écran Alertes WhatsApp: ce que le serveur
 * contrôle tout seul (session, adresse de réception, file d'envoi, message
 * témoin du matin), en une grille de cases label / valeur.
 *
 * Rien ne s'affiche tant que le serveur n'envoie pas `watchdog`. Les cases se
 * répartissent en 1, 2 ou 3 colonnes selon la place (six cases: jamais de case
 * seule sur sa ligne); les adresses longues passent à la ligne, jamais de
 * défilement horizontal. Les teintes sont sobres: le rouge n'est pas utilisé.
 */
export default function SurveillanceWhatsapp({ watchdog, maintenant }) {
  const resume = resumeSurveillance(watchdog, maintenant);
  if (!resume) return null;

  return (
    <Card className="card-base" data-testid="surveillance-whatsapp">
      <CardHeader className="space-y-1">
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          {libelle('surveillanceWhatsapp', 'titre')}
        </CardTitle>
        <CardDescription
          data-testid="surveillance-verification"
          className={cn(resume.verification.perimee && 'text-flame-ink')}
        >
          {resume.verification.libelle} : {resume.verification.texte}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {resume.lignes.map((ligne) => (
            <div
              key={ligne.cle}
              data-testid={`surveillance-${ligne.cle}`}
              className="min-w-0 space-y-1.5 rounded-lg border border-border bg-muted/30 p-3"
            >
              <dt className="text-xs font-medium text-muted-foreground">{ligne.libelle}</dt>
              <dd className="min-w-0">
                {ligne.etat ? (
                  <StatutBadge
                    domaine="surveillanceWhatsapp"
                    code={ligne.etat.code}
                    className="max-w-full whitespace-normal text-left leading-snug"
                  >
                    {ligne.etat.texte}
                  </StatutBadge>
                ) : ligne.attention ? (
                  <StatutBadge
                    ton="warning"
                    className="max-w-full whitespace-normal text-left leading-snug"
                  >
                    {ligne.valeur}
                  </StatutBadge>
                ) : (
                  <span className={cn('text-sm font-medium', ligne.vide && 'text-muted-foreground')}>
                    {ligne.valeur}
                  </span>
                )}
              </dd>
              {ligne.details.map((detail) => (
                <dd key={`${detail.intitule || ''}${detail.texte}`} className="min-w-0 text-xs text-muted-foreground">
                  {detail.intitule && <span>{detail.intitule} : </span>}
                  <span
                    className={cn(
                      detail.selectionnable && 'select-all break-all font-mono text-foreground',
                    )}
                  >
                    {detail.texte}
                  </span>
                </dd>
              ))}
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
